import type {
  RuleBasedEditFieldColumn,
  RuleBasedEditFieldValue,
} from '../../types';
import type { I18nLocale } from '@ardoq/i18n';
import { t } from '@ardoq/i18n';
import { FormSize, NumberInput, TextArea, TextInput } from '@ardoq/forms';
import { Multiselect, Select, SelectOption } from '@ardoq/select';
import { DateOnlyPicker, DateTimePicker } from '@ardoq/date-time-picker';
import {
  formatDateShort,
  formatDateTimeShort,
  parseLocaleDateString,
} from '@ardoq/date-time';
import type { Locale } from '@ardoq/locale';

const getCheckboxOptions = (i18nLocale: I18nLocale): SelectOption<string>[] => [
  {
    label: t(i18nLocale, 'inventory:cellRenderers.checkbox.checked', 'Checked'),
    value: 'true',
  },
  {
    label: t(
      i18nLocale,
      'inventory:cellRenderers.checkbox.unchecked',
      'Unchecked'
    ),
    value: 'false',
  },
];

const toDate = (value: RuleBasedEditFieldValue, locale: Locale): Date | null =>
  typeof value === 'string' && value !== ''
    ? parseLocaleDateString(value, locale)
    : null;

/**
 * Named on the option so the user knows what it would write. Select options
 * carry the readable form of a stored value — a user field's default is an id,
 * and a date field's is an ISO string.
 */
export const formatDefaultValue = (
  { defaultValue, inputType, options }: RuleBasedEditFieldColumn,
  i18nLocale: I18nLocale,
  locale: Locale
): string => {
  if (typeof defaultValue === 'boolean') {
    return getCheckboxOptions(i18nLocale)[defaultValue ? 0 : 1].label;
  }
  const asText = String(defaultValue);
  if (inputType === 'date' || inputType === 'dateTime') {
    const date = toDate(defaultValue, locale);
    const format = inputType === 'date' ? formatDateShort : formatDateTimeShort;
    return date ? format(date, locale) : asText;
  }
  return options.find(option => option.value === asText)?.label ?? asText;
};

type BulkEditFieldValueInputProps = {
  column: RuleBasedEditFieldColumn;
  value: RuleBasedEditFieldValue;
  /**
   * Only on the dialog's first render. Focusing the value input when the user
   * changes the field would move focus off the field select they are still on
   * (WCAG 3.2.2).
   */
  shouldAutoFocus: boolean;
  i18nLocale: I18nLocale;
  locale: Locale;
  onValueChange: (value: RuleBasedEditFieldValue) => void;
};

export const BulkEditFieldValueInput = ({
  column,
  value,
  shouldAutoFocus,
  i18nLocale,
  locale,
  onValueChange,
}: BulkEditFieldValueInputProps) => {
  const label = column.label;

  switch (column.inputType) {
    case 'number':
      return (
        <NumberInput
          allowDecimals
          autoFocus={shouldAutoFocus}
          aria-label={label}
          formSize={FormSize.FULL}
          value={typeof value === 'number' ? value : undefined}
          onValueChange={newValue =>
            onValueChange(Number.isFinite(newValue) ? newValue : null)
          }
        />
      );

    case 'textArea':
      return (
        <TextArea
          autoFocus={shouldAutoFocus}
          aria-label={label}
          formSize={FormSize.FULL}
          value={typeof value === 'string' ? value : ''}
          onValueChange={onValueChange}
        />
      );

    case 'checkbox':
      return (
        <Select
          autoFocus={shouldAutoFocus}
          aria-label={label}
          formSize={FormSize.FULL}
          i18nLocale={i18nLocale}
          options={getCheckboxOptions(i18nLocale)}
          value={typeof value === 'boolean' ? String(value) : null}
          onValueChange={selected => onValueChange(selected === 'true')}
        />
      );

    case 'select':
      return (
        <Select
          autoFocus={shouldAutoFocus}
          isClearable
          aria-label={label}
          formSize={FormSize.FULL}
          i18nLocale={i18nLocale}
          options={column.options}
          value={typeof value === 'string' ? value : null}
          onValueChange={onValueChange}
        />
      );

    case 'multiselect':
      return (
        <Multiselect
          isMulti
          autoFocus={shouldAutoFocus}
          aria-label={label}
          formSize={FormSize.FULL}
          i18nLocale={i18nLocale}
          options={column.options}
          value={Array.isArray(value) ? value : []}
          onValueChange={selected => onValueChange(selected ?? [])}
        />
      );

    case 'date':
      return (
        <DateOnlyPicker
          autoFocus={shouldAutoFocus}
          aria-label={label}
          date={toDate(value, locale)}
          config={{ locale, formatter: formatDateShort }}
          i18nLocale={i18nLocale}
          onChange={onValueChange}
        />
      );

    case 'dateTime':
      return (
        <DateTimePicker
          autoFocus={shouldAutoFocus}
          aria-label={label}
          date={toDate(value, locale)}
          config={{
            locale,
            useTimePicker: true,
            formatter: formatDateTimeShort,
          }}
          i18nLocale={i18nLocale}
          onChange={date => onValueChange(date ? date.toISOString() : null)}
        />
      );

    case 'text':
      return (
        <TextInput
          autoFocus={shouldAutoFocus}
          aria-label={label}
          formSize={FormSize.FULL}
          value={typeof value === 'string' ? value : ''}
          onValueChange={onValueChange}
        />
      );

    default:
      column.inputType satisfies never;
      return null;
  }
};
